<template>
	<div class="js-router-app"></div>
</template>
<script setup lang="ts">
	import { 
		watch, 
		computed, 
		nextTick, 
		onMounted, 
		defineEmits
	} from 'vue';
	import { useStore } from 'vuex';
	import { useRouter, useRoute } from 'vue-router'
	import { 
		IFiltersState, 
		IFilterQueryStringParameters, 
		ILocation, 
		IFilterOption 
	} from '@/types';
	import { EFilterQueryStringParameters, EStockCategoryId } from '@/enums';
	import { ESortOptions } from '@/enums';

	const route = useRoute();
	const router = useRouter();
	const store = useStore();

	const emit = defineEmits(['ready']);

	const hasMadeInitialUpdateVehicles = computed(() => store.state.hasMadeInitialUpdateVehicles);
	const isReady = computed(() => hasMadeInitialUpdateVehicles.value);
	const isRetrievingData = computed(() => store.state.isRetrievingData);

	const getNewFilterState = (): IFiltersState => {
		const f: IFiltersState =  {
			conditions: [],//array<ILocation>,
			bodies: [],//array<IFilterOption>,
			certified: [],//array<IFilterOption>,
			fuels: [],//array<IFilterOption>,
			locations: [],//array<ILocation>,
			makes: [],//array<IFilterOption>,
			models: [],//array<IFilterOption>,
			transmissions: [],//array<IFilterOption>,
			priceMin: 0,
			priceMax: 0,
			yearMin: 0, 
			yearMax: 0	
		};
		return f;
	};
	const initCondition = async (f: IFiltersState): Promise<Array<EStockCategoryId>> => {
		const cond: Array<number> = route.query.condition?.toString().split('+').map(c => parseInt(c)) || [];
		const newCond = !cond.length ? 
			route.fullPath.includes(((window as any).uvDealerUsedVehUrl)) ? [EStockCategoryId.used] : 
			route.fullPath.includes(((window as any).uvDealerNewVehUrl)) ? [EStockCategoryId.new] : 
			route.fullPath.includes(((window as any).uvDealerDemoVehUrl)) ? [EStockCategoryId.demo] : 
			[EStockCategoryId.new, EStockCategoryId.used, EStockCategoryId.demo] : 
			cond
		;
		cond.forEach((c: number, i: number) => {
			if (isNaN(c)) delete cond[i];
		});
		newCond.forEach((c) => {
			f.conditions.push({
				Count: -1,
				LocationName: c == EStockCategoryId.used ? "Used" : c == EStockCategoryId.new ? 'New' : c == EStockCategoryId.demo ? 'Demo' : '',
				LocationId: c,
				Phone: null,
				Selected: true
			});
		});
		await router.push({ query: {...route.query, condition: newCond.join('+')}});
		return newCond as Array<EStockCategoryId>;
	};
	const getNextStringFromPath = (startStr: string) => {
		const str = ((startStr.length-1) != startStr.lastIndexOf('/') ? startStr + '/' : startStr).toLowerCase();
		if (!str) return '';
		const startIndex = route.path.toLowerCase().indexOf(str);
		if (startIndex == -1) return '';
		const start = startIndex + str.length;
		const nextSlash = route.path.indexOf('/', start + 1);
		const end = nextSlash != -1 ? nextSlash : route.path.length;
		const newStr = route.path.substring(start, end);
		return newStr;
	};
	const initMake = async (f: IFiltersState, actualCond: Array<EStockCategoryId>): Promise<Array<string>> => {
		const condStr = 
			actualCond.length == 1 && actualCond.includes(EStockCategoryId.used) ? (window as any).uvDealerUsedVehUrl : 
			actualCond.length == 1 && actualCond.includes(EStockCategoryId.new) ? (window as any).uvDealerNewVehUrl : 
			actualCond.length == 1 && actualCond.includes(EStockCategoryId.demo) ? (window as any).uvDealerDemoVehUrl : 
			(window as any).uvDealerFallbackVehUrl || ''
		;
		const newMake = getNextStringFromPath(condStr);
		const actualMakes = pushFilterOptionToFilter(f.makes, (route.query.makes?.toString() || newMake));
		await router.push({ query: {...route.query, makes: actualMakes.map(m => m.toLowerCase()).join('+')}});
		return Promise.resolve(actualMakes || []);
	};
	const initModel = async (f: IFiltersState, newMakes: Array<string>): Promise<void> => {
		if (!newMakes.length) return Promise.resolve();
		let allModels: Array<string> = [];
		const newModel = getNextStringFromPath(newMakes[0]);
		const actualModels = pushFilterOptionToFilter(f.models, (route.query.models?.toString() || newModel));
		actualModels.forEach((mo: string) => {
			allModels.push(mo);
		});
		await router.push({ query: {...route.query, models: allModels.join('+')}});
		return Promise.resolve();
	};
	const initConditionMakeModel = async (f: IFiltersState): Promise<void> => {
		const actualCond: Array<EStockCategoryId> = await initCondition(f);
		const actualMake: Array<string> = await initMake(f, actualCond);
		await initModel(f, actualMake);
		return Promise.resolve();
	};
	const pushFilterOptionToFilter = (f: Array<IFilterOption>, str: string): Array<string> => {
		if (!str) return [];
		const arr = str.split('+');
		arr.forEach((o) => {
			f.push({
				Count: -1,
				Item: o,
				InfoItem: null,
				Selected: true	
			});
		});
		return arr;
	};
	const initOtherFilters = async (f: IFiltersState): Promise<void> => {
		const obj: IFilterQueryStringParameters = getObjectFromQueryString();
		pushFilterOptionToFilter(f.bodies, obj.body);
		pushFilterOptionToFilter(f.certified, obj.certified);
		pushFilterOptionToFilter(f.fuels, obj.fuel);
		pushFilterOptionToFilter(f.transmissions, obj.transmission);

		const cMin = parseInt(obj.cMin || "");
		const cMax = parseInt(obj.cMax || "");
		f.priceMin = !isNaN(cMin) ? cMin : 0;
		f.priceMax = !isNaN(cMax) ? cMax : 0;
		const yMin = parseInt(obj.yMin || "");
		const yMax = parseInt(obj.yMax || "");
		f.yearMin = !isNaN(yMin) ? yMin : 0;
		f.yearMax = !isNaN(yMax) ? yMax : 0;
		
		return Promise.resolve();
	};
	const getObjectFromQueryString = (): IFilterQueryStringParameters => {
		return {
			[EFilterQueryStringParameters.priceMin]: route.query[EFilterQueryStringParameters.priceMin]?.toString() || '', 
			[EFilterQueryStringParameters.priceMax]: route.query[EFilterQueryStringParameters.priceMax]?.toString() || '', 
			[EFilterQueryStringParameters.yearMin]: route.query[EFilterQueryStringParameters.yearMin]?.toString() || '', 
			[EFilterQueryStringParameters.yearMax]: route.query[EFilterQueryStringParameters.yearMax]?.toString() || '', 
			[EFilterQueryStringParameters.locations]: route.query[EFilterQueryStringParameters.locations]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.conditions]: route.query[EFilterQueryStringParameters.conditions]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.makes]: route.query[EFilterQueryStringParameters.makes]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.certified]: route.query[EFilterQueryStringParameters.certified]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.models]: route.query[EFilterQueryStringParameters.models]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.body]: route.query[EFilterQueryStringParameters.body]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.fuel]: route.query[EFilterQueryStringParameters.fuel]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.transmission]: route.query[EFilterQueryStringParameters.transmission]?.toString().toLowerCase() || '', 
			[EFilterQueryStringParameters.sort]: route.query[EFilterQueryStringParameters.sort]?.toString() || ''
		};
	};
	const getQueryStringFromFilters = (): IFilterQueryStringParameters => {
		const nf: IFiltersState = store.state.filters;
		const obj: IFilterQueryStringParameters = {
			[EFilterQueryStringParameters.priceMin]: nf.priceMin.toString(),
			[EFilterQueryStringParameters.priceMax]: nf.priceMax.toString(),
			[EFilterQueryStringParameters.yearMin]: nf.yearMin.toString(),
			[EFilterQueryStringParameters.yearMax]: nf.yearMax.toString(),
			[EFilterQueryStringParameters.locations]: nf.locations.filter((o: ILocation) => o.Selected).map((o: ILocation) => o.LocationId).join('+').toLowerCase(),
			[EFilterQueryStringParameters.conditions]: nf.conditions.filter((o: ILocation) => o.Selected).map((o: ILocation) => o.LocationId).join('+').toLowerCase(),
			[EFilterQueryStringParameters.makes]: nf.makes.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.certified]: nf.certified.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.models]: nf.models.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.body]: nf.bodies.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.fuel]: nf.fuels.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.transmission]: nf.transmissions.filter((o: IFilterOption) => o.Selected).map((o: IFilterOption) => o.Item).join('+').toLowerCase(),
			[EFilterQueryStringParameters.sort]: store.state.sort
		};
		return obj;
	};
	const checkForNewFilters = (): void => {
		const newObj = getQueryStringFromFilters();
		const currentObj = getObjectFromQueryString();
		if (JSON.stringify(newObj) != JSON.stringify(currentObj)){
			updateQueryString(newObj);	
		}
	};
	const updateQueryString = (obj: IFilterQueryStringParameters): void => {
		router.push({ query: {...obj} });
	};

	onMounted(async () => {
		const f: IFiltersState = getNewFilterState();
		await initConditionMakeModel(f);
		await initOtherFilters(f);
		store.commit('filters/setFilters',f);

		const sort = route.query.sort || ESortOptions.JustListed;
		store.commit('setSort', sort);
		
		nextTick(() => store.dispatch('updateVehicles', { amend: false, firstCall: true }));
	});

	watch(isRetrievingData, (v) => {
		if (!v) {
			nextTick(() => {
				checkForNewFilters();
			});
		}
	});
	watch(isReady, (v: boolean) => {
		if (v){
			nextTick(() => {
				emit('ready', true);
			});
		}
	});
</script>